import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import * as authApi from '../api/auth'
import { isDevAuthBypass } from '../lib/devAuth'

// Computed once at module load. In a `vite build` production bundle,
// import.meta.env.DEV is the literal `false`, so this is a compile-time
// constant `false` and every branch guarded by it below is dead code that
// tree-shaking (and even just the JS engine) never executes in production.
const DEV_AUTH_BYPASS = isDevAuthBypass(import.meta.env)

// Synthetic user for VITE_DEV_BYPASS_AUTH=1 dev sessions — never touches
// the network. Fields are inert placeholders satisfying authApi.User.
const DEV_BYPASS_USER: authApi.User = {
  username: 'dev-bypass',
  email: null,
  disabled: false,
}

type AuthContextValue = {
  user: authApi.User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<authApi.User | null>(DEV_AUTH_BYPASS ? DEV_BYPASS_USER : null)
  const [isLoading, setIsLoading] = useState(!DEV_AUTH_BYPASS)
  const navigate = useNavigate()

  // Initial auth probe: call /me. If the cookie is valid, we get the
  // current user. If not (expired/missing/no cookie), we get 401 and
  // the user state stays null.
  useEffect(() => {
    // Dead code in production (DEV is compile-time false there): skip the
    // network probe entirely, the synthetic user is already in state.
    if (DEV_AUTH_BYPASS) return
    let cancelled = false
    authApi
      .getCurrentUser()
      .then((u) => {
        if (!cancelled) setUser(u)
      })
      .catch(() => {
        if (!cancelled) setUser(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Listen for the api/client.ts custom event fired on 401 responses.
  // When any API call returns 401, treat the session as expired:
  // clear user state and redirect to /login.
  useEffect(() => {
    // Dead code in production (DEV is compile-time false there): a 401 from
    // a missing/expired agent token must not bounce a bypass session to
    // /login — panels show their own error states instead.
    if (DEV_AUTH_BYPASS) return
    function onUnauthorized() {
      setUser(null)
      navigate('/login', { replace: true })
    }
    window.addEventListener('auth:unauthorized', onUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized)
  }, [navigate])

  const login = useCallback(async (username: string, password: string) => {
    await authApi.login({ username, password })
    // Cookie is now set; fetch the user details to populate state.
    const u = await authApi.getCurrentUser()
    setUser(u)
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Even if the API call fails (already-expired session, etc.),
      // proceed to clear local state. The cookie may already be gone.
    }
    setUser(null)
    navigate('/login', { replace: true })
  }, [navigate])

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: user !== null,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return ctx
}
